import styled from 'styled-components'

import {
  useRegionTrendingListingsQuery,
  useGetBucketListItemsIndexQuery,
} from '../../../../hooks/useQueries'

import useIsUserSignedIn from '../../../../hooks/useIsUserSignedIn'

import ListingCard from '../../../../components/cards/ListingCard'
import WithSelectEscape from '../../../../components/SelectEscape/WithSelectEscape'
import PaginatedSlides from '../../../../components/display/PaginatedSlides'

import { IntroTitle } from '../styled'

// Matches the Intro section's vertical rhythm. Lives inside the component so a
// hidden block leaves no stray margins behind.
const Wrapper = styled.section`
  margin-top: 40px;
  margin-bottom: 40px;
`

type TrendingListing = GQL_ListingCardFragmentFragment

export const TrendingStayItem = ({
  item,
  noCardTag = true,
}: {
  item: TrendingListing
  noCardTag?: boolean
}) => {
  // Skip for anonymous (edge-cached) visitors so the slider never fires the
  // per-user query without a user; it can only return their own index.
  const { isSignedIn } = useIsUserSignedIn()
  const { data: bucketListItemsIndexQueryData } =
    useGetBucketListItemsIndexQuery({ skip: !isSignedIn })

  const bucketListItemsIndex =
    bucketListItemsIndexQueryData?.currentUser?.bucketListItemsIndex

  return (
    <WithSelectEscape
      itemId={item.id}
      itemType="Listing"
      bucketListItemsIndex={bucketListItemsIndex}
    >
      <ListingCard listing={item} noCardTag={noCardTag} />
    </WithSelectEscape>
  )
}

type TrendingStaysProps = {
  market: GQL_MarketCode
  regionId: string
  regionName: string
}

const noop = () => {}

const TrendingStays = ({
  market,
  regionId,
  regionName,
}: TrendingStaysProps) => {
  // Non-lazy + cache-first: reads the SSR seed on the server and the first
  // client render with loading=false, so the cards land in the server HTML
  // without a loading state or hydration mismatch.
  const { data, called } = useRegionTrendingListingsQuery({
    variables: { market, regionId },
    fetchPolicy: 'cache-first',
  })

  const listings = data?.explore?.region?.trendingListings

  if (!listings?.length) return null

  return (
    <Wrapper>
      <PaginatedSlides
        customTitle={
          <IntroTitle>
            Trending <em>{regionName}</em> Stays
          </IntroTitle>
        }
        called={called}
        loading={false}
        canFetchMore={false}
        onFetchMore={noop}
        items={listings}
        ItemComponent={TrendingStayItem}
        minHeight="0"
      />
    </Wrapper>
  )
}

export default TrendingStays
